import { Activity, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "../../components/ui/badge";

interface EvalSuite {
  id: string;
  name: string;
  metrics: { name: string; value: number; baseline: number; target: number }[];
  lastRun: string;
}

const SUITES: EvalSuite[] = [
  {
    id: "rag_multilingual",
    name: "RAG multilingüe (ES/EN)",
    lastRun: "2026-05-15T07:00:00Z",
    metrics: [
      { name: "retrieval_recall@5", value: 0.92, baseline: 0.9, target: 0.85 },
      { name: "retrieval_recall@10", value: 0.96, baseline: 0.94, target: 0.9 },
      { name: "citation_accuracy", value: 0.94, baseline: 0.93, target: 0.9 },
      { name: "language_correctness", value: 0.98, baseline: 0.97, target: 0.95 },
      { name: "answer_groundedness", value: 0.88, baseline: 0.86, target: 0.8 },
      { name: "acl_leakage_rate", value: 0, baseline: 0, target: 0 },
    ],
  },
  {
    id: "autolink_positives",
    name: "Autolink positivos",
    lastRun: "2026-05-14T22:00:00Z",
    metrics: [
      { name: "precision_high", value: 0.97, baseline: 0.96, target: 0.95 },
      { name: "recall_high", value: 0.82, baseline: 0.79, target: 0.75 },
      { name: "generic_terms_suppressed", value: 0.93, baseline: 0.91, target: 0.9 },
    ],
  },
  {
    id: "parser_positions",
    name: "Parser positions",
    lastRun: "2026-05-14T22:00:00Z",
    metrics: [
      { name: "section_line_accuracy", value: 1, baseline: 1, target: 1 },
      { name: "chunk_size_p50_within_band", value: 0.94, baseline: 0.93, target: 0.9 },
    ],
  },
];

export function EvalsDashboard() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Activity size={20} /> Admin · Evals
        </h1>
        <p className="text-sm text-(--text-secondary)">
          Métricas de calidad: retrieval, citas, idioma, autolinks, parser. Bloquean PR si caen
          &gt;2pp.
        </p>
      </header>

      <div className="space-y-4">
        {SUITES.map((s) => (
          <section
            key={s.id}
            className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4"
          >
            <header className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="font-semibold">{s.name}</h2>
                <p className="text-xs text-(--text-tertiary)">
                  ID {s.id} · última ejecución {new Date(s.lastRun).toLocaleString()}
                </p>
              </div>
              <Badge variant={s.metrics.every((m) => m.value >= m.target) ? "success" : "error"}>
                {s.metrics.every((m) => m.value >= m.target) ? "PASS" : "FAIL"}
              </Badge>
            </header>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-(--text-tertiary)">
                <tr>
                  <th className="py-1">Métrica</th>
                  <th>Valor</th>
                  <th>Baseline</th>
                  <th>Target</th>
                  <th>Δ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {s.metrics.map((m) => {
                  const ok = m.value >= m.target;
                  const delta = m.value - m.baseline;
                  return (
                    <tr key={m.name} className="border-t border-(--border-subtle)">
                      <td className="py-1 font-mono text-xs">{m.name}</td>
                      <td>{m.value.toFixed(3)}</td>
                      <td className="text-(--text-tertiary)">{m.baseline.toFixed(3)}</td>
                      <td className="text-(--text-tertiary)">{m.target.toFixed(3)}</td>
                      <td className={delta >= 0 ? "text-(--success)" : "text-(--error)"}>
                        {delta >= 0 ? "+" : ""}
                        {delta.toFixed(3)}
                      </td>
                      <td>
                        {ok ? (
                          <CheckCircle2 size={14} className="text-(--success)" />
                        ) : (
                          <XCircle size={14} className="text-(--error)" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}
